import type { APIClient } from '@src/shared/api';

import type { TaskReference } from './types';

type TaskReferenceRouteSearch = Record<string, unknown> & {
  tab?: string;
  mode?: string;
};

export type TaskReferenceRoute =
  | {
      to: '/users/$userId/edit';
      params: {
        userId: string;
      };
      search: TaskReferenceRouteSearch;
    }
  | {
      to: '/absence';
      search?: {
        absenceId: number;
        /** ISO date of the absence, so the calendar can reach it. */
        date: string;
      };
    };

type TaskRouteResolverParams = {
  apiClient: APIClient;
  objectId: number | null;
  urlParams: string;
  search: TaskReferenceRouteSearch;
};

type TaskRouteResolver = (
  params: TaskRouteResolverParams,
) => TaskReferenceRoute | null | Promise<TaskReferenceRoute | null>;

type ScheduleReferenceDto = {
  dwts_user: number;
};

type AbsenceReferenceDto = {
  acti_date: string;
};

function getSearchFromUrlParams(
  urlParams: string,
  searchExtra: TaskReferenceRouteSearch,
): TaskReferenceRouteSearch {
  const search: TaskReferenceRouteSearch = {};

  for (const param of (urlParams || '').split('.')) {
    if (['edit', 'view'].includes(param)) {
      search.mode = param;
    } else {
      search.tab = param;
    }
  }

  return {
    ...search,
    ...searchExtra,
  };
}

const taskRouteRegistry: Record<string, TaskRouteResolver> = {
  'users.user': ({ objectId, urlParams, search }) => {
    if (objectId === null) {
      return null;
    }

    return {
      to: '/users/$userId/edit',
      params: {
        userId: String(objectId),
      },
      search: getSearchFromUrlParams(urlParams, search),
    };
  },
  'users.defaultworkingtimeschedule': async ({ apiClient, objectId }) => {
    if (objectId === null) {
      return null;
    }

    try {
      const schedule = (await apiClient.request({
        url: `users/schedules/${String(objectId)}/`,
      })) as ScheduleReferenceDto;

      return {
        to: '/users/$userId/edit',
        params: {
          userId: String(schedule.dwts_user),
        },
        search: {
          mode: 'view',
          tab: 'schedule',
        },
      };
    } catch {
      return null;
    }
  },
  'activity.activityabsence': async ({ apiClient, objectId }) => {
    if (objectId === null) {
      return { to: '/absence' };
    }

    // The absence page only loads the visible range, and referenced absences
    // are usually in a future month, so carry the date along to reach it.
    try {
      const absence = (await apiClient.request({
        url: `activity/absence/${String(objectId)}/`,
      })) as AbsenceReferenceDto;

      return {
        to: '/absence',
        search: { absenceId: objectId, date: absence.acti_date },
      };
    } catch {
      return { to: '/absence' };
    }
  },
};

export function canResolveTaskReferenceRoute(
  reference: TaskReference,
): boolean {
  const [referenceType = ''] = reference.type?.split(':', 1) ?? [];
  return referenceType in taskRouteRegistry;
}

export async function resolveTaskReferenceRoute(
  reference: TaskReference,
  apiClient: APIClient,
): Promise<TaskReferenceRoute | null> {
  if (!reference.type) {
    return null;
  }

  const [referenceType, urlParams = ''] = reference.type?.split(':', 2) ?? [''];

  const resolver = taskRouteRegistry[referenceType];
  if (!resolver) {
    return null;
  }

  return resolver({
    apiClient,
    objectId: reference.objectId,
    urlParams,
    search: reference.urlParams,
  });
}
